import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, Subject, switchMap, take, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';

// ── Refresh state ─────────────────────────────────────────────────────────────
// Subject (not BehaviorSubject) so waiting requests do NOT receive a stale null
// on subscribe — they only unblock when the in-flight refresh actually settles.

let isRefreshing = false;
const refreshResult$ = new Subject<string | null>();

// ── Interceptor ───────────────────────────────────────────────────────────────

export const JwtInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  const isPublic = environment.auth.publicPaths.some(path => req.url.includes(path));
  if (isPublic) return next(req);

  const token = auth.getToken();
  if (!token) return next(req);

  return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })).pipe(
    catchError(err => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        return handle401(auth, req, next);
      }
      return throwError(() => err);
    })
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function attachBearer<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

/**
 * Called when any non-public request receives a 401.
 *
 * Only one refresh is ever in flight at a time.  All other concurrent 401s
 * wait on refreshResult$ and are retried (or rejected) together once the
 * refresh settles.
 */
function handle401(
  auth: AuthService,
  originalReq: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;

    return auth.refreshSession().pipe(
      switchMap(success => {
        isRefreshing = false;
        const newToken = success ? auth.getToken() : null;
        refreshResult$.next(newToken);
        if (newToken) {
          return next(attachBearer(originalReq, newToken));
        }
        return throwError(() => new HttpErrorResponse({ status: 401 }));
      }),
      catchError(err => {
        isRefreshing = false;
        refreshResult$.next(null);
        return throwError(() => err);
      })
    );
  }

  // Another request already triggered a refresh — wait for its result
  return refreshResult$.pipe(
    take(1),
    switchMap(token => {
      if (!token) return throwError(() => new HttpErrorResponse({ status: 401 }));
      return next(attachBearer(originalReq, token));
    })
  );
}
